import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "examples/Tables/DataTable";
import Add from "@mui/icons-material/Add";
import { CrmPage, CrmPageHeader, CrmActionButton, CrmTableCard, CrmNotice, CrmRowActions, ListTruncate, ListLink } from "../shared/CrmUi";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import CrudService from "services/cruds-service";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from "chart.js";
import { appConfirm } from "components/AppDialog";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

function ProspectListTable() {
    const [data, setData] = useState([]);          // Ham veri
    const [tableData, setTableData] = useState([]); // Tabloya yansıyacak satırlar
    const [notification, setNotification] = useState({ value: false, text: "" });

    const navigate = useNavigate();


    const fetchData = useCallback(async () => {
        try {
            const response = await CrudService.getProspectLists();

            const normalized = response.data.map(({ id, attributes }) => ({
                id,               // 24 hanelik ObjectId
                ...attributes,    // attributes’in içinde artık id yok
            }));


            setData(normalized);
        } catch (err) {
            console.error("Error fetching target customer lists:", err);
            setNotification({ value: true, text: "Veriler yüklenemedi. Lütfen tekrar deneyin." });
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getRows = useCallback((list) => {
        return list.map((strategy, index) => {
            const markets = strategy.keyMarkets || [];
            const selected = strategy.selectedKeyMarkets;
            const totalScore = (() => {
                if (Array.isArray(selected) && selected.length > 0) {
                    const result = markets.reduce((acc, km, idx) => {
                        const kmId = km.id || km._id || `${strategy.id}-${idx}`;
                        const isSelected = selected.includes(kmId);
                        return isSelected ? acc + Number(km.score || 0) : acc;
                    }, 0);
                    return result;
                }

                if (selected && typeof selected === "object" && Object.keys(selected).length > 0) {
                    const result = Object.entries(selected).reduce((sum, [strategyId, selectedKms]) => {
                        return sum + markets.reduce((acc, km, idx) => {
                            const kmId = km.id || km._id || `${strategyId}-${idx}`;
                            const isSelected = selectedKms.includes(kmId);
                            return isSelected ? acc + Number(km.score || 0) : acc;
                        }, 0);
                    }, 0);
                    return result;
                }

                const result = markets.reduce((sum, m, idx) => {
                    const score = Number(m.score || 0);
                    return sum + score;
                }, 0);
                return result;
            })();

            return {
                id: strategy.id,
                strategyName: (
                    <ListLink onClick={() =>
                                    navigate(
                                        `/target/create-lead/details?type=prospect&id=${strategy._id || strategy.id}`
                                    )}><ListTruncate text={strategy.leadCompany || "—"} max={40} /></ListLink>
                ),
                region: markets.map((m) => m.region).filter(Boolean).join(", "),
                incoterms: markets
                    .flatMap((m) => m.selectedIncoterm || [])
                    .map((i) => (typeof i === "string" ? i : i.code || i.name || ""))
                    .filter(Boolean)
                    .join(", "),
                shippingPreferences: markets
                    .flatMap((m) => m.shippingPreferences || [])
                    .map((s) => (typeof s === "string" ? s : s.prefName || s.name || ""))
                    .filter(Boolean)
                    .join(", "),
                industries: markets
                    .flatMap((m) => m.industry || [])
                    .map((ind) => (typeof ind === "string" ? ind : ind.name || ind.title || ""))
                    .filter(Boolean)
                    .join(", "),
                actions: (
                    <CrmRowActions onEdit={() =>
                                    navigate(
                                        `/target/create-lead/details?type=prospect&id=${strategy._id || strategy.id}`
                                    )} editTitle="Edit Prospect" onDelete={(e) => clickDeleteHandler(e, strategy.id)} deleteTitle="Delete Prospect" />
                ),
            };
        });
    }, []);


    useEffect(() => {
        setTableData(getRows(data));
    }, [data, getRows]);


    const clickDeleteHandler = useCallback(
        async (_e, id) => {
            try {
                if (!await appConfirm("Are you sure you want to delete this Prospect List?")) return;

                await CrudService.deleteProspectList(id);

                /* Local state’ten çıkar → tablo otomatik güncellenir */
                setData((prev) => prev.filter((item) => item.id !== id));

                setNotification({ value: true, text: "ProspectList successfully deleted." });
            } catch (err) {
                console.error("Error deleting target customer lists:", err);
                setNotification({
                    value: true,
                    text: "Deletion failed. Please try again.",
                });
            } finally {
                /* Bildirimi 5 sn sonra kapat */
                setTimeout(() => setNotification({ value: false, text: "" }), 5000);
            }
        },
        [setData],
    );

    const clickAddHandler = useCallback(() => {
        navigate("/strategy/create-strategy/details");
    }, [navigate]);


    const columns = useMemo(
        () => [
            {
                Header: "Strategy",
                accessor: "strategyName",
            },
            {
                Header: "Region",
                accessor: "region",
            },
            {
                Header: "Incoterms",
                accessor: "incoterms",
            },
            {
                Header: "Shipping&nbsp;Prefs",
                accessor: "shippingPreferences",
            },
            {
                Header: "Industries",
                accessor: "industries",
            },
            {
                Header: "Actions",
                accessor: "actions",
                disableSortBy: true,
            },
        ],
        [],
    );


    const dataTableData = useMemo(
        () => ({ columns, rows: tableData }),
        [columns, tableData],
    );

    /* ------------------- RENDER ------------------- */
    return (
        <DashboardLayout>
            <DashboardNavbar />

            <CrmPage>
                <CrmPageHeader
                    title="Target Customer List"
                    subtitle="Prospect companies selected for active sales strategies."
                    actions={
                        <CrmActionButton icon={<Add />} onClick={clickAddHandler}>
                            Create Strategy
                        </CrmActionButton>
                    }
                />
                {notification.value && <CrmNotice tone="blue">{notification.text}</CrmNotice>}
                <CrmTableCard title="Target Customers" summary={`${tableData.length} records`}>
                    <DataTable table={dataTableData} canSearch />
                </CrmTableCard>
            </CrmPage>

            <Footer />
        </DashboardLayout>
    );
}

export default ProspectListTable;
